import { LitElement, html, css } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { consume } from "@lit/context";
import { configContext, lockedContext } from "../contexts";
import type { Attribute, RatingConfig, VersionConfig } from "../models";

/**
 * Renders a single rateable attribute as a number-line selector.
 */
@customElement("bab-attribute-rater")
export class BabAttributeRater extends LitElement {
  static styles = css`
    :host {
      display: block;
    }

    .attribute-header {
      position: relative;
      margin-bottom: 2px;
    }

    .attribute-name {
      font-size: 0.8rem;
      font-weight: var(--bab-font-weight, 600);
      margin: 0;
      color: var(--bab-label-color, #111827);
      line-height: 1.2;
      /* Reserve space for the label so name wraps at a consistent point */
      padding-right: 100px;
      min-height: 1rem;
    }

    .current-label {
      position: absolute;
      top: 0;
      right: 0;
      font-size: 0.7rem;
      font-weight: var(--bab-font-weight, 600);
      color: var(--bab-accent, #4a90e2);
      white-space: nowrap;
      transition: opacity 0.15s, visibility 0.15s;
      opacity: 0;
      visibility: hidden;
      line-height: 1;
    }

    .current-label--visible {
      opacity: 1;
      visibility: visible;
    }

    .attribute-description {
      font-size: 0.7rem;
      color: var(--bab-description-color, #6b7280);
      margin: 0 0 8px 0;
      line-height: 1.4;
    }

    .rating-buttons {
      display: flex;
      align-items: center;
    }

    .rating-buttons--compact button {
      width: 24px;
      height: 24px;
      font-size: 0.65rem;
    }

    /* Track segments sit between buttons in the DOM — no z-index needed */
    .track-segment {
      flex: 1;
      height: var(--bab-border-width, 2px);
      background: var(--bab-button-border, #d1d5db);
      transition: background 0.15s;
    }

    .track-segment--filled {
      background: var(--bab-accent, #4a90e2);
    }

    /* Circular nodes */
    button {
      flex-shrink: 0;
      width: 32px;
      height: 32px;
      padding: 0;
      border: var(--bab-border-width, 2px) solid var(--bab-button-border, #d1d5db);
      border-radius: 50%;
      background: var(--bab-button-bg, #f9fafb);
      color: var(--bab-button-color, #374151);
      font-size: 0.75rem;
      font-weight: var(--bab-font-weight, 600);
      font-family: inherit;
      cursor: pointer;
      transition:
        background 0.15s,
        border-color 0.15s,
        color 0.15s,
        transform 0.1s;
    }

    button:hover:not(:disabled) {
      border-color: var(--bab-accent, #4a90e2);
      color: var(--bab-accent, #4a90e2);
      background: var(--bab-button-bg, #fff);
    }

    button:active:not(:disabled) {
      transform: scale(0.93);
    }

    button:disabled {
      opacity: 0.45;
      cursor: not-allowed;
    }

    button:focus-visible {
      outline: 2px solid var(--bab-accent, #4a90e2);
      outline-offset: 2px;
    }

    button[aria-pressed="true"] {
      background: var(--bab-accent, #4a90e2);
      border-color: var(--bab-accent, #4a90e2);
      color: #fff;
    }

    button.button--dot {
      width: 12px;
      height: 12px;
      margin: 10px; /* (32 - 12) / 2 to keep spacing consistent */
      font-size: 0;
    }

    .rating-buttons--compact button.button--dot {
      width: 10px;
      height: 10px;
      margin: 7px; /* (24 - 10) / 2 */
    }

    /* Min / max labels below the track */
    .endpoint-labels {
      display: flex;
      justify-content: space-between;
      margin-top: 5px;
      font-size: 0.65rem;
      font-weight: var(--bab-font-weight, 500);
      color: var(--bab-rating-label-color, #6b7280);
    }
  `;

  /** The attribute definition to display and rate. */
  @property({ type: Object })
  attribute!: Attribute;

  /** Current rating value (minRating–maxRating). null means unrated. */
  @property({ type: Number })
  rating: number | null = null;

  @consume({ context: configContext, subscribe: true })
  @property({ type: Object })
  config: RatingConfig = {
    currentVersion: "v1",
    versions: { v1: { attributeIds: [], minRating: 1, maxRating: 5 } },
    defaultLabels: {},
    attributes: [],
  };

  @consume({ context: lockedContext, subscribe: true })
  @property({ type: Boolean })
  disabled = false;

  /**
   * The version config for the currently active version. When provided,
   * overrides `config.currentVersion` for rating range and labels.
   */
  @property({ type: Object })
  versionConfig?: VersionConfig;

  @state() private _hoveredRating: number | null = null;

  private get _activeVersionConfig(): VersionConfig {
    return this.versionConfig ?? this.config.versions[this.config.currentVersion];
  }

  private _currentLabel(n: number): string {
    const labels = this.attribute.labels ?? this.config.defaultLabels;
    return labels[n] ?? String(n);
  }

  private _select(value: number | null) {
    if (this.disabled) return;
    this.dispatchEvent(
      new CustomEvent("rating-changed", {
        detail: { attributeId: this.attribute.id, rating: value },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private _onKeyDown(e: KeyboardEvent) {
    if (this.disabled) return;
    if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
    e.preventDefault();
    const delta = e.key === "ArrowRight" ? 1 : -1;
    const { minRating, maxRating } = this._activeVersionConfig;
    
    let next: number | null;
    if (this.rating === null) {
      next = delta > 0 ? minRating : null;
    } else {
      next = this.rating + delta;
      if (next < minRating) next = null;
      if (next !== null && next > maxRating) next = maxRating;
    }

    if (next === this.rating) return;
    this._select(next);
    
    if (next !== null) {
      const buttons = this.shadowRoot!.querySelectorAll("button");
      const focusIndex = next - minRating;
      const button = buttons[focusIndex] as HTMLButtonElement;
      if (button) button.focus();
    }
  }

  private _endpointLabels(): { min: string; max: string } {
    const labels = this.attribute.labels ?? this.config.defaultLabels;
    const { minRating, maxRating } = this._activeVersionConfig;
    return {
      min: labels[minRating] ?? String(minRating),
      max: labels[maxRating] ?? String(maxRating),
    };
  }

  render() {
    const { min, max } = this._endpointLabels();
    const displayRating = this._hoveredRating ?? this.rating;
    const { minRating, maxRating, labelInterval = 1 } = this._activeVersionConfig;
    const range = Array.from({ length: maxRating - minRating + 1 }, (_, i) => minRating + i);
    const isCompact = range.length > 6;

    return html`
      <div class="attribute-header">
        <p class="attribute-name">${this.attribute.name}</p>
        <span class="current-label ${displayRating !== null ? "current-label--visible" : ""}" 
              role="status" aria-live="polite">
          ${displayRating !== null ? html`${displayRating} — ${this._currentLabel(displayRating)}` : ""}
        </span>
      </div>
      ${this.attribute.description
        ? html`<p class="attribute-description">
            ${this.attribute.description}
          </p>`
        : ""}
      <div
        class="rating-buttons ${isCompact ? "rating-buttons--compact" : ""}"
        role="group"
        aria-label="Rating for ${this.attribute.name}"
        @keydown="${this._onKeyDown}"
        @mouseleave="${() => { this._hoveredRating = null; }}"
      >
        ${range.map(
          (n) => {
            const hasLabel = (n - minRating) % labelInterval === 0;
            return html`
              ${n > minRating ? html`<span class="track-segment ${displayRating !== null && displayRating >= n ? "track-segment--filled" : ""}" aria-hidden="true"></span>` : ""}
              <button
                class="${!hasLabel ? "button--dot" : ""}"
                aria-pressed="${this.rating === n ? "true" : "false"}"
                aria-label="${n} — ${this._currentLabel(n)}"
                tabindex="${this.rating === n || (this.rating === null && n === minRating)
                  ? "0"
                  : "-1"}"
                ?disabled="${this.disabled}"
                @click="${() => this._select(n)}"
                @mouseenter="${() => { this._hoveredRating = n; }}"
              >
                ${hasLabel ? n : ""}
              </button>
            `;
          },
        )}
      </div>
      <div class="endpoint-labels">
        <span>${min}</span>
        <span>${max}</span>
      </div>
    `;
  }
}


declare global {
  interface HTMLElementTagNameMap {
    "bab-attribute-rater": BabAttributeRater;
  }
}
